import { useEffect, useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  type ColumnDef,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { toast } from 'sonner';
import { useAdminApiQuery, useAdminApiMutation } from '@/hooks/use-admin-api-query';
import { qk } from '@/lib/query/keys';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ConfirmationDialog } from '@/components/super-admin/confirmation-dialog';
import { DataTable } from '@/components/super-admin/data-table';
import { WorkspaceMembersList } from '@/components/super-admin/workspace-members-list';
import { DetailLoadError } from '@/components/super-admin/detail-load-error';
import { ChevronRight, ChevronLeft, Check, Copy } from 'lucide-react';
import { PageContainer } from '@/components/page-container';
import { unitLabel, unitTitle } from '@/lib/usage-unit';

interface Member {
  id: string;
  userId: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  role: string;
  createdAt: string;
}

interface OrgChannel {
  id: string;
  type: 'whatsapp' | 'instagram' | 'facebook';
  displayName: string | null;
  whatsappDisplayPhoneNumber: string | null;
  instagramUsername: string | null;
  facebookPageName: string | null;
  tokenStatus: string;
  forwardingEnabled: boolean;
  metaConnected: boolean;
  /** Usage this quota period, in the org's meter (AIT-436). */
  used: number;
  workspace: { id: string; name: string };
  createdAt: string;
}

interface OrganizationDetail {
  organization: { id: string; name: string; createdAt: string };
  planSlug: string;
  subscription: {
    planSlug: string;
    status: string;
    currentPeriodEnd: string | null;
    customActionQuota: number | null;
    customPriceCents: number | null;
    customInterval: 'month' | 'year' | null;
    customStripePriceId: string | null;
  } | null;
  members: Member[];
  workspaces: { id: string; name: string; channelCount: number }[];
  channels: OrgChannel[];
  usage: {
    used: number;
    usageUnit: 'messages' | 'actions';
    limit: number;
    percentage: number;
  };
}

interface CompResult {
  outcome: 'paid' | 'pay_failed';
  status: string | null;
  planSlug: string | null;
  invoiceId: string | null;
  invoiceTotalCents: number | null;
}

const COMP_PLANS = ['build', 'scale', 'business'] as const;

interface CardLink {
  publicId: string;
  recipientEmail: string | null;
  status: string;
  completedAt: string | null;
  createdAt: string;
  url: string;
}

function formatShortDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function memberLabel(member: Member): string {
  const name = [member.firstName, member.lastName].filter(Boolean).join(' ');
  return name || member.email || member.userId;
}

function CopyLinkButton({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(url);
          setCopied(true);
          toast.success('Link copied');
          setTimeout(() => setCopied(false), 1500);
        } catch {
          toast.error('Failed to copy link');
        }
      }}
    >
      {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
      Copy Link
    </Button>
  );
}

export function SuperAdminOrganizationDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [channelSearch, setChannelSearch] = useState('');
  const [selectedRecipientId, setSelectedRecipientId] = useState('');
  const [revokeTarget, setRevokeTarget] = useState<CardLink | null>(null);
  const [compPlan, setCompPlan] = useState<(typeof COMP_PLANS)[number]>('build');
  const [compCoupon, setCompCoupon] = useState('internal_100_forever');
  const [compResult, setCompResult] = useState<CompResult | null>(null);
  const [billingConfirm, setBillingConfirm] = useState<'comp' | 'forwarding' | 'custom' | 'clearCustom' | null>(null);
  const [customQuota, setCustomQuota] = useState('');
  const [customPrice, setCustomPrice] = useState('');
  const [customInterval, setCustomInterval] = useState<'month' | 'year'>('month');

  const { data = null, isLoading, isError, error, refetch } = useAdminApiQuery<OrganizationDetail>(
    qk.superAdmin('organizations', id),
    `/super-admin/organizations/${id}`,
  );

  const cardLinksKey = qk.superAdmin('organizations', id, 'card-links');
  const { data: cardLinksData } = useAdminApiQuery<{ links: CardLink[] }>(
    cardLinksKey,
    `/super-admin/organizations/${id}/card-links`,
  );
  const cardLinks = cardLinksData?.links ?? [];

  // Defaults the recipient select to the first admin, falling back to the
  // first member — an org can have no admin among synced members.
  const orgMembers = data?.members ?? [];
  const defaultRecipientId =
    orgMembers.find((m) => m.role === 'admin')?.userId ?? orgMembers[0]?.userId ?? '';
  const recipientId = selectedRecipientId || defaultRecipientId;

  const invalidateCardLinks = () =>
    void queryClient.invalidateQueries({ queryKey: cardLinksKey });

  const createCardLink = useAdminApiMutation<
    { publicId: string; url: string },
    { recipientUserPublicId?: string }
  >(
    (vars) => ({
      path: `/super-admin/organizations/${id}/card-links`,
      init: { method: 'POST', body: JSON.stringify(vars) },
      fallback: 'Failed to create card link',
    }),
    {
      onSuccess: () => {
        toast.success('Card link created');
        invalidateCardLinks();
      },
      onError: (err) => toast.error(err.userMessage ?? 'Failed to create card link'),
    },
  );

  const revokeCardLink = useAdminApiMutation<{ ok: boolean }, string>(
    (publicId) => ({
      path: `/super-admin/card-links/${publicId}`,
      init: { method: 'DELETE' },
      fallback: 'Failed to revoke card link',
    }),
    {
      onSuccess: () => {
        toast.success('Card link revoked');
        invalidateCardLinks();
      },
      onError: (err) => toast.error(err.userMessage ?? 'Failed to revoke card link'),
      onSettled: () => setRevokeTarget(null),
    },
  );

  const invalidateOrg = () =>
    void queryClient.invalidateQueries({ queryKey: qk.superAdmin('organizations', id) });

  const compOrg = useAdminApiMutation<CompResult, { planSlug: string; couponId?: string }>(
    (vars) => ({
      path: `/super-admin/organizations/${id}/comp`,
      init: { method: 'POST', body: JSON.stringify(vars) },
      fallback: 'Failed to unpause organization',
    }),
    {
      onSuccess: (res) => {
        setCompResult(res);
        if (res.outcome === 'paid') toast.success('Organization unpaused');
        else toast.error('Resumed, but the invoice was not paid');
        invalidateOrg();
      },
      onError: (err) => toast.error(err.userMessage ?? 'Failed to unpause organization'),
      onSettled: () => setBillingConfirm(null),
    },
  );

  // AIT-738 — custom plan for a v2 org: own action quota, optional own price.
  const setCustomPlan = useAdminApiMutation<
    { ok: boolean },
    { clear: true } | { actionQuota: number; priceCents?: number; interval?: 'month' | 'year' }
  >(
    (vars) => ({
      path: `/super-admin/organizations/${id}/custom-plan`,
      init: { method: 'PUT', body: JSON.stringify(vars) },
      fallback: 'Failed to save custom plan',
    }),
    {
      onSuccess: () => {
        toast.success('Custom plan saved');
        invalidateOrg();
      },
      onError: (err) => toast.error(err.userMessage ?? 'Failed to save custom plan'),
      onSettled: () => setBillingConfirm(null),
    },
  );
  // Hydrate the cadence from the current deal, so re-pricing a yearly plan
  // never silently converts it to monthly.
  const currentCustomInterval = data?.subscription?.customInterval ?? null;
  useEffect(() => {
    if (currentCustomInterval) setCustomInterval(currentCustomInterval);
  }, [currentCustomInterval]);
  const customQuotaValue = Number(customQuota);
  const customQuotaValid = /^\d+$/.test(customQuota.trim()) && customQuotaValue > 0;
  const customPriceValid = customPrice.trim() === '' || /^\d+(\.\d{1,2})?$/.test(customPrice.trim());

  const enableForwarding = useAdminApiMutation<{ enabledCount: number }, void>(
    () => ({
      path: `/super-admin/organizations/${id}/enable-forwarding`,
      init: { method: 'POST' },
      fallback: 'Failed to enable forwarding',
    }),
    {
      onSuccess: (res) => {
        toast.success(`Forwarding enabled on ${res.enabledCount} channel${res.enabledCount === 1 ? '' : 's'}`);
        invalidateOrg();
      },
      onError: (err) => toast.error(err.userMessage ?? 'Failed to enable forwarding'),
      onSettled: () => setBillingConfirm(null),
    },
  );

  // Memoized: an unmemoized array remounts every cell on each render.
  const channelRows = useMemo(() => data?.channels ?? [], [data]);

  const channelColumns = useMemo<ColumnDef<OrgChannel>[]>(
    () => [
      {
        accessorKey: 'type',
        header: 'Type',
        enableSorting: true,
        enableGlobalFilter: false,
        meta: { headerClassName: 'w-[110px]', sortLabel: 'type' },
        cell: ({ row }) =>
          row.original.type === 'instagram' ? 'Instagram' : row.original.type === 'facebook' ? 'Facebook' : 'WhatsApp',
      },
      {
        accessorKey: 'displayName',
        header: 'Name',
        enableSorting: true,
        meta: { cellClassName: 'font-medium', sortLabel: 'name' },
      },
      {
        id: 'workspace',
        accessorFn: (c) => c.workspace.name,
        header: 'Workspace',
        enableSorting: true,
        meta: { sortLabel: 'workspace' },
        cell: ({ row }) => (
          <Link
            to={`/super-admin/workspaces/${row.original.workspace.id}`}
            className="text-primary hover:underline"
          >
            {row.original.workspace.name}
          </Link>
        ),
      },
      {
        accessorKey: 'forwardingEnabled',
        header: 'Messaging',
        enableSorting: true,
        enableGlobalFilter: false,
        meta: { headerClassName: 'w-[110px]', sortLabel: 'messaging' },
        cell: ({ row }) => (
          <span
            className={
              row.original.forwardingEnabled ? 'text-green-600' : 'text-muted-foreground'
            }
          >
            {row.original.forwardingEnabled ? 'Enabled' : 'Disabled'}
          </span>
        ),
      },
      {
        accessorKey: 'used',
        header: unitTitle(data?.usage.usageUnit),
        enableSorting: true,
        enableGlobalFilter: false,
        meta: { headerClassName: 'w-[140px]', cellClassName: 'tabular-nums', sortLabel: 'usage' },
        cell: ({ row }) =>
          `${row.original.used.toLocaleString()} ${unitLabel(data?.usage.usageUnit)}`,
      },
      {
        id: 'link',
        header: '',
        enableGlobalFilter: false,
        meta: { headerClassName: 'w-[110px]' },
        cell: ({ row }) => (
          <Link
            to={`/super-admin/channels/${row.original.id}`}
            className="text-sm text-primary hover:underline"
          >
            View details
          </Link>
        ),
      },
    ],
    [data?.usage.usageUnit],
  );

  const channelTable = useReactTable({
    data: channelRows,
    columns: channelColumns,
    getRowId: (row) => row.id,
    defaultColumn: { enableSorting: false },
    enableMultiSort: false,
    sortDescFirst: false,
    state: { globalFilter: channelSearch },
    onGlobalFilterChange: setChannelSearch,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  if (isError) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-1 text-sm text-muted-foreground">
          <Link to="/super-admin/organizations" className="hover:text-foreground">
            Organizations
          </Link>
          <ChevronRight className="h-3 w-3" />
          <span>...</span>
        </div>
        <DetailLoadError
          error={error}
          entity="Organization"
          backTo="/super-admin/organizations"
          backLabel="Back To Organizations"
          onRetry={() => void refetch()}
        />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-1 text-sm text-muted-foreground">
          <Link to="/super-admin/organizations" className="hover:text-foreground">
            Organizations
          </Link>
          <ChevronRight className="h-3 w-3" />
          <Skeleton className="h-4 w-24" />
        </div>
        <Skeleton className="h-7 w-48" />
        <Card>
          <CardContent className="space-y-3 py-4">
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!data) return null;

  return (
    <PageContainer variant="fluid" className="space-y-4">
      <Button
        variant="ghost"
        size="sm"
        className="-ml-2"
        onClick={() => navigate('/super-admin/organizations')}
      >
        <ChevronLeft className="h-4 w-4" />
        Back to organizations
      </Button>

      <div className="flex items-center gap-1 text-sm text-muted-foreground">
        <Link to="/super-admin/organizations" className="hover:text-foreground">
          Organizations
        </Link>
        <ChevronRight className="h-3 w-3" />
        <span>{data.organization.name}</span>
      </div>

      <div className="flex items-center gap-2">
        <h1 className="text-xl font-semibold">Organization: {data.organization.name}</h1>
        <Badge variant="secondary" className="capitalize">
          {data.planSlug} plan
        </Badge>
      </div>

      <div className="flex items-center gap-3">
        <span className="text-sm text-muted-foreground">Usage this quota period</span>
        <span className="text-base font-semibold tabular-nums">
          {data.usage.used.toLocaleString()} / {data.usage.limit.toLocaleString()}
        </span>
        <span className="text-sm text-muted-foreground">{unitLabel(data.usage.usageUnit)}</span>
        <div className="h-2 w-40 overflow-hidden rounded-full bg-muted">
          <div
            className={`h-full ${data.usage.percentage >= 100 ? 'bg-destructive' : 'bg-primary'}`}
            style={{ width: `${Math.min(100, data.usage.percentage)}%` }}
          />
        </div>
        <span className="text-sm text-muted-foreground">{data.usage.percentage}%</span>
      </div>

      <Tabs defaultValue="channels">
        <TabsList>
          <TabsTrigger value="channels">Channels</TabsTrigger>
          <TabsTrigger value="users">Users</TabsTrigger>
          <TabsTrigger value="workspaces">Workspaces</TabsTrigger>
          <TabsTrigger value="card-links">Card Links</TabsTrigger>
          <TabsTrigger value="billing">Billing</TabsTrigger>
        </TabsList>

        <TabsContent value="channels" className="mt-4">
          {data.channels.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <h3 className="text-xl font-semibold">No channels connected</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                This organization has no connected channels.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <Input
                placeholder="Search channels by name or workspace..."
                aria-label="Search channels"
                value={channelSearch}
                onChange={(e) => setChannelSearch(e.target.value)}
                className="max-w-sm"
              />
              <div className="rounded-md border">
                <DataTable table={channelTable} emptyState="No channels match that search." />
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="users" className="mt-4">
          <WorkspaceMembersList members={data.members} />
        </TabsContent>

        <TabsContent value="workspaces" className="mt-4">
          <Card>
            <CardContent className="space-y-2 py-4">
              {data.workspaces.length === 0 && (
                <p className="text-sm text-muted-foreground">No workspaces.</p>
              )}
              {data.workspaces.map((w) => (
                <div key={w.id} className="flex justify-between text-sm">
                  <Link
                    to={`/super-admin/workspaces/${w.id}`}
                    className="text-primary hover:underline"
                  >
                    {w.name}
                  </Link>
                  <span className="text-muted-foreground">
                    {w.channelCount} channel{w.channelCount === 1 ? '' : 's'}
                  </span>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="card-links" className="mt-4 space-y-4">
          <div className="flex items-center gap-2">
            <Select
              value={recipientId}
              onValueChange={setSelectedRecipientId}
              disabled={orgMembers.length === 0}
            >
              <SelectTrigger aria-label="Card link recipient" className="w-64">
                <SelectValue placeholder="Select a member">
                  {(value: string) => {
                    const m = orgMembers.find((mm) => mm.userId === value);
                    return m ? memberLabel(m) : null;
                  }}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {orgMembers.map((m) => (
                  <SelectItem key={m.userId} value={m.userId}>
                    {memberLabel(m)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              type="button"
              disabled={createCardLink.isPending}
              onClick={() =>
                createCardLink.mutate(
                  recipientId ? { recipientUserPublicId: recipientId } : {},
                )
              }
            >
              Create Card Link
            </Button>
          </div>

          {cardLinks.length === 0 ? (
            <p className="text-sm text-muted-foreground">No card links yet.</p>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Recipient</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cardLinks.map((link) => (
                    <TableRow key={link.publicId}>
                      <TableCell>{link.recipientEmail ?? 'N/A'}</TableCell>
                      <TableCell className="capitalize">
                        {link.completedAt ? 'completed' : link.status}
                      </TableCell>
                      <TableCell>{formatShortDate(link.createdAt)}</TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          <CopyLinkButton url={link.url} />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="text-destructive hover:text-destructive"
                            onClick={() => setRevokeTarget(link)}
                          >
                            Revoke
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>
        <TabsContent value="billing" className="mt-4 space-y-6">
          <p className="text-sm">
            Subscription:{' '}
            <span className="font-medium capitalize">
              {data.subscription ? `${data.subscription.status}, ${data.subscription.planSlug}` : 'none'}
            </span>
          </p>

          <div className="space-y-2">
            <h3 className="text-sm font-semibold">Unpause / Comp</h3>
            <div className="flex items-center gap-2">
              <Select value={compPlan} onValueChange={(v) => setCompPlan(v as (typeof COMP_PLANS)[number])}>
                <SelectTrigger aria-label="Comp plan" className="w-40 capitalize">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COMP_PLANS.map((p) => (
                    <SelectItem key={p} value={p} className="capitalize">
                      {p}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                aria-label="100% coupon"
                placeholder="100% coupon (optional)"
                value={compCoupon}
                onChange={(e) => setCompCoupon(e.target.value)}
                className="w-64"
              />
              <Button
                type="button"
                disabled={data.subscription?.status !== 'paused' || compOrg.isPending}
                onClick={() => setBillingConfirm('comp')}
              >
                Unpause
              </Button>
            </div>
            {compResult && (
              <p className="text-sm text-muted-foreground">
                {compResult.outcome === 'paid' ? 'Paid' : 'Payment failed'}: status {compResult.status ?? 'unknown'}, plan{' '}
                {compResult.planSlug ?? 'unknown'}, invoice{' '}
                {compResult.invoiceTotalCents === null ? 'none' : `$${(compResult.invoiceTotalCents / 100).toFixed(2)}`}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <h3 className="text-sm font-semibold">Custom Plan</h3>
            <p className="text-sm text-muted-foreground">
              {data.subscription?.customActionQuota != null || data.subscription?.customStripePriceId
                ? `Custom: ${(data.subscription.customActionQuota ?? 0).toLocaleString()} actions${
                    data.subscription.customPriceCents != null
                      ? `, $${(data.subscription.customPriceCents / 100).toFixed(2)} per ${data.subscription.customInterval ?? 'month'}`
                      : ', plan price'
                  }`
                : 'None. The org gets its tier\'s catalog quota and price.'}
            </p>
            <div className="flex items-center gap-2">
              <Input
                aria-label="Action quota"
                placeholder="Action quota"
                inputMode="numeric"
                value={customQuota}
                onChange={(e) => setCustomQuota(e.target.value)}
                className="w-40"
              />
              <Input
                aria-label="Price in dollars"
                placeholder="Price $ (blank = keep)"
                inputMode="decimal"
                value={customPrice}
                onChange={(e) => setCustomPrice(e.target.value)}
                className="w-48"
              />
              <Select value={customInterval} onValueChange={(v) => setCustomInterval(v as 'month' | 'year')}>
                <SelectTrigger aria-label="Billing interval" className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="month">Monthly</SelectItem>
                  <SelectItem value="year">Yearly</SelectItem>
                </SelectContent>
              </Select>
              <Button
                type="button"
                disabled={!customQuotaValid || !customPriceValid || setCustomPlan.isPending}
                onClick={() => setBillingConfirm('custom')}
              >
                Save Custom Plan
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={
                  (data.subscription?.customActionQuota == null && !data.subscription?.customStripePriceId) ||
                  setCustomPlan.isPending
                }
                onClick={() => setBillingConfirm('clearCustom')}
              >
                Clear
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <h3 className="text-sm font-semibold">Forwarding</h3>
            <Button
              type="button"
              variant="outline"
              disabled={enableForwarding.isPending}
              onClick={() => setBillingConfirm('forwarding')}
            >
              Enable Forwarding On All Connected Channels
            </Button>
          </div>
        </TabsContent>
      </Tabs>

      <ConfirmationDialog
        open={billingConfirm !== null}
        onOpenChange={(open) => {
          if (!open) setBillingConfirm(null);
        }}
        title={
          billingConfirm === 'comp'
            ? 'Unpause this organization?'
            : billingConfirm === 'custom'
              ? 'Save this custom plan?'
              : billingConfirm === 'clearCustom'
                ? 'Clear the custom plan?'
                : 'Enable forwarding on all channels?'
        }
        description={
          billingConfirm === 'comp'
            ? `Moves the subscription to ${compPlan}${compCoupon.trim() ? ` with coupon ${compCoupon.trim()}` : ' at full price, charging the card on file'} and resumes it now.`
            : billingConfirm === 'custom'
              ? `${customQuotaValue.toLocaleString()} actions a month${
                  customPrice.trim()
                    ? `, billed $${Number(customPrice).toFixed(2)} per ${customInterval}. The Stripe subscription switches to this price now, prorated.`
                    : ' on the current price.'
                }`
              : billingConfirm === 'clearCustom'
                ? 'Restores the tier\'s catalog quota. A custom price switches back to the catalog price now, prorated.'
                : 'Every connected channel in this organization starts forwarding again, whatever paused it.'
        }
        confirmLabel={
          billingConfirm === 'comp'
            ? 'Unpause'
            : billingConfirm === 'custom'
              ? 'Save Custom Plan'
              : billingConfirm === 'clearCustom'
                ? 'Clear Custom Plan'
                : 'Enable Forwarding'
        }
        loading={compOrg.isPending || enableForwarding.isPending || setCustomPlan.isPending}
        onConfirm={() => {
          if (billingConfirm === 'comp') {
            const couponId = compCoupon.trim();
            compOrg.mutate({ planSlug: compPlan, ...(couponId ? { couponId } : {}) });
          } else if (billingConfirm === 'custom') {
            setCustomPlan.mutate({
              actionQuota: customQuotaValue,
              ...(customPrice.trim()
                ? { priceCents: Math.round(Number(customPrice) * 100), interval: customInterval }
                : {}),
            });
          } else if (billingConfirm === 'clearCustom') {
            setCustomPlan.mutate({ clear: true });
          } else {
            enableForwarding.mutate();
          }
        }}
      />

      <ConfirmationDialog
        open={revokeTarget !== null}
        onOpenChange={(open) => {
          if (!open) setRevokeTarget(null);
        }}
        title="Revoke this card link?"
        description="The link stops working immediately. This cannot be undone."
        confirmLabel="Revoke Link"
        variant="destructive"
        loading={revokeCardLink.isPending}
        onConfirm={() => {
          if (revokeTarget) revokeCardLink.mutate(revokeTarget.publicId);
        }}
      />
    </PageContainer>
  );
}
